import PropTypes from "prop-types";
import { useMemo } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";

// project-imports
import DrawerHeader from "./DrawerHeader";
import DrawerContent from "./DrawerContent";
import MiniDrawerStyled from "./MiniDrawerStyled";

import { DRAWER_WIDTH, DRAWER_SLOT_WIDTH, MINI_DRAWER_SLOT_WIDTH } from "config";
import { handlerDrawerOpen, useGetMenuMaster } from "api/menu";
import { useSelector } from "react-redux";
import { canAccessAdminPanel } from "utils/adminAccess";
import { drawerPaperSx } from "./drawerV2Tokens";
import "./drawerV2.css";

const FULL_WIDTH_BREAKPOINT = 450;

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }) {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down("lg"));
  const fullWidthMobile = useMediaQuery(`(max-width:${FULL_WIDTH_BREAKPOINT}px)`);
  const user = useSelector((state) => state.auth.user);

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const handleClose = () => handlerDrawerOpen(false);

  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(
    () => (
      <DrawerHeader
        open={drawerOpen}
        fullWidthMobile={downLG && fullWidthMobile}
        onClose={handleClose}
      />
    ),
    [drawerOpen, downLG, fullWidthMobile],
  );

  return (
    <Box
      component="nav"
      className="drawer-v2"
      sx={{
        flexShrink: { md: 0 },
        zIndex: 1200,
        width: {
          lg: drawerOpen ? DRAWER_SLOT_WIDTH : MINI_DRAWER_SLOT_WIDTH,
        },
      }}
      aria-label="mailbox folders"
    >
      {!downLG ? (
        <MiniDrawerStyled
          variant="permanent"
          open={drawerOpen}
          isAdmin={canAccessAdminPanel(user)}
        >
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={handleClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: fullWidthMobile ? "100%" : DRAWER_WIDTH,
              maxWidth: fullWidthMobile ? "100vw" : DRAWER_WIDTH,
              ...drawerPaperSx,
            },
          }}
        >
          {drawerHeader}
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}

MainDrawer.propTypes = { window: PropTypes.func };
